import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import { EmptyState, Loading, PageHeader, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { OutboxStatus, ViaText } from './outboxShared.jsx';

const LEARN = 'learn@check.summitshield.io';

/** Your own official emails we saw ourselves: cc'd to learn@, or sent to your own lists. */
export default function Outbox() {
  const { organisation } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || '');
  const page = Number(params.get('page')) || 1;

  useEffect(() => {
    if (!organisation) return;
    setData(null);
    api(`/portal/organisations/${organisation.id}/outbox?page=${page}`).then(setData).catch((err) => setError(err.message));
  }, [organisation, page, reload]);

  const title = 'Emails you copied to us';
  const subtitle = `Your own official emails that we saw ourselves. Cc ${LEARN} on your campaigns and announcements, or add contacts to your own lists from an event’s page. Each one we can prove came from you is kept on record, so when an attendee forwards us a copy, we tell them it’s genuine.`;
  if (error) return <div className="page"><PageHeader title={title} /><Alert tone="danger">{error}</Alert></div>;

  return (
    <div className="page">
      <PageHeader title={title} subtitle={subtitle} />
      {notice && <Alert tone="success">{notice}</Alert>}
      {data && data.counts.recorded + data.counts.not_recorded > 0 && (
        <div className="watch-grid outbox-stats">
          <div className="watch-tile"><span className="muted">On record as yours</span><strong>{data.counts.recorded}</strong><span className="muted">Emails we hold a proven copy of</span></div>
          <div className="watch-tile"><span className="muted">Copies people checked</span><strong>{data.counts.confirmations}</strong><span className="muted">Told it really was from you</span></div>
          {data.counts.not_recorded > 0 && <div className="watch-tile"><span className="muted">Couldn’t be proven</span><strong className="tone-text-warn">{data.counts.not_recorded}</strong><span className="muted">Sent in a way we couldn’t check</span></div>}
        </div>
      )}

      <Section flush={!!data?.messages.length}>
        {!data ? <Loading /> : data.messages.length === 0 ? (
          <EmptyState title="None of your emails yet">
            Next time you send a campaign, an announcement or a ticket email, add <strong>{LEARN}</strong> as a Cc or Bcc.
            It appears here, and attendees who forward us a copy are told it’s really from you.
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack inbox-table">
              <thead><tr><th>Received</th><th>Email</th><th>How we saw it</th><th>Status</th><th>Checked by others</th>{data.can_delete && <th><span className="sr-only">Delete</span></th>}</tr></thead>
              <tbody>
                {data.messages.map((m) => (
                  <tr key={m.id}>
                    <td data-label="Received" className="nowrap">{fmtDateTime(m.received_at)}</td>
                    <td data-label="Email">
                      <Link className="strong-link" to={`/outbox/${m.id}`}>{m.subject || '(no subject)'}</Link>
                      <div className="muted small">
                        {m.from_address ? `From ${m.from_name || ''}${m.from_name ? ' ' : ''}<${m.from_address}>` : 'Sender unknown'}
                        {m.service && <> · via {m.service}</>}
                      </div>
                    </td>
                    <td data-label="How we saw it"><ViaText via={m.via} /></td>
                    <td data-label="Status"><OutboxStatus status={m.status} reason={m.reason} /></td>
                    <td data-label="Checked by others">{m.confirmations > 0 ? `${m.confirmations} ${m.confirmations === 1 ? 'person' : 'people'}` : <span className="muted">—</span>}</td>
                    {data.can_delete && (
                      <td className="right row-actions">
                        <button type="button" className="btn btn-small btn-danger-ghost" title="Temporary, while testing" onClick={() => setDeleting(m)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {deleting && (
        <ConfirmModal title="Delete this email?" confirmLabel="Delete" danger onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await api(`/portal/organisations/${organisation.id}/outbox/${deleting.id}`, { method: 'DELETE' });
            setDeleting(null); setNotice('Deleted.'); setReload((n) => n + 1);
          }}>
          <p>“{deleting.subject || '(no subject)'}” disappears along with its record, so copies people forward won’t be confirmed by it any more. Temporary, while testing.</p>
        </ConfirmModal>
      )}

      {data && (page > 1 || data.has_more) && (
        <div className="pager">
          {page > 1 && <button type="button" className="btn btn-secondary btn-small" onClick={() => setParams({ page: String(page - 1) })}>Previous</button>}
          <span className="muted small">Page {page}</span>
          {data.has_more && <button type="button" className="btn btn-secondary btn-small" onClick={() => setParams({ page: String(page + 1) })}>Next</button>}
        </div>
      )}
    </div>
  );
}
